// src/components/Header.js
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import Navigation from './Navigation';
import TokenPrice from './common/TokenPrice';
import EthPrice from './common/EthPrice';

// Import explicite du CSS 
import './Header.css';

// Import des logos
import LogoImage from '../assets/logo.jpg';
import EthLogo from '../assets/eth-logo.png';
import MetamaskLogo from '../assets/metamask-logo.png';

const Header = () => {
    const { 
        account, 
        connectWallet, 
        disconnectWallet, 
        isConnecting,
        formatAddress,
        error
    } = useWeb3();
    
    // Référence pour la navigation
    const navRef = useRef(null);
    
    // État pour l'indicateur de défilement
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    
    // Vérifier si la navigation est scrollable
    useEffect(() => {
        const checkScrollable = () => {
            if (navRef.current) {
                const { scrollWidth, clientWidth } = navRef.current;
                setShowScrollIndicator(scrollWidth > clientWidth);
            }
        };
        
        checkScrollable();
        window.addEventListener('resize', checkScrollable);
        
        return () => {
            window.removeEventListener('resize', checkScrollable);
        };
    }, []);
    
    // Gestion de la connexion/déconnexion
    const handleConnectWallet = async () => {
        if (account) {
            await disconnectWallet();
        } else {
            await connectWallet();
        }
    };
    
    return (
        <header className="header-main">
            <div className="header-content">
                {/* Section de gauche: tokens et wallet */}
                <div className="header-top-row">
                    {/* Prix des tokens */}
                    <div className="tokens-container">
                        {/* Modifié: Ajout du lien externe vers Dextools */}
                        <a 
                            href="https://www.dextools.io/app/fr/ether/pair-explorer/0x8a1d8f57261e8832ce1d7c525df76dbe002b2e25?t=1743605630155" 
                            className="token-item"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img 
                                src={LogoImage}
                                alt="GR33D" 
                                className="token-logo gr33d-logo"
                            />
                            <span className="token-price glow-effect">
                                <TokenPrice />
                            </span>
                        </a>
                        
                        {/* Modifié: Ajout du lien externe vers CoinMarketCap */}
                        <a 
                            href="https://coinmarketcap.com/currencies/ethereum/" 
                            className="token-item"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img 
                                src={EthLogo}
                                alt="ETH" 
                                className="token-logo eth-logo"
                            />
                            <span className="token-price">
                                <EthPrice />
                            </span>
                        </a>
                    </div>
                    
                    {/* Bouton wallet */}
                    <div className="wallet-section">
                        {error && (
                            <div className="wallet-error">
                                {error}
                            </div>
                        )}
                        
                        <button 
                            className={`wallet-button ${account ? "wallet-connected" : "wallet-disconnected"}`}
                            onClick={handleConnectWallet}
                            disabled={isConnecting}
                        >
                            <div className="wallet-button-content">
                                {isConnecting ? (
                                    <div className="wallet-connecting">
                                        <span className="loading-dots"></span>
                                    </div>
                                ) : account ? (
                                    <div className="wallet-info">
                                        <span className="wallet-action">Disconnect</span>
                                        <span className="wallet-address">{formatAddress(account)}</span>
                                    </div>
                                ) : (
                                    <div className="wallet-connect">
                                        <div className="connect-text">
                                            <span className="connect-line">Connect</span>
                                            <span className="connect-line">Wallet</span>
                                        </div>
                                        <img 
                                            src={MetamaskLogo} 
                                            alt="Metamask"
                                            className="metamask-logo"
                                        />
                                    </div>
                                )}
                            </div>
                        </button>
                    </div>
                </div>
                
                {/* Section de droite: navigation */}
                <div className="header-bottom-row">
                    <div className="navigation-wrapper">
                        <div 
                            className="navigation-container"
                            ref={navRef}
                        >
                            <Navigation account={account} isLoading={isConnecting} />
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default React.memo(Header);
